import type { InteractiveProps } from './types'
import { memo, useEffect, useRef } from 'react'
import type {
  ContentActions,
  ContentRenderOptions,
  ContentSpec,
  JesterConfig,
} from '@blakeelearning/content-loader-render'
import { renderJester } from '@blakeelearning/content-loader-render'
import { useObjectFlagValue, useOpenFeatureClient } from '@openfeature/react-sdk'
import { Logger, useLogger } from '@/parts/logger/hook'
import { useConfiguration } from '@/parts/configuration/wrapper'

function useJesterFeatureFlags() {
  const client = useOpenFeatureClient()
  const { payload: flagNames } = useObjectFlagValue<{ payload: string[] }>(
    'msp_jester-flag-names',
    { payload: [] },
  )

  // flagNames is a new array instance every render (from useObjectFlagValue),
  // so filtering it fresh each time would hand every caller of
  // JesterInteractive a new array on every render. Feature flags are fetched
  // once at app load and are stable for the life of the app, so - matching
  // how GameInteractive freezes its configuration - resolve this once and
  // freeze it for the life of the mount.
  // eslint-disable-next-line react-hooks/refs
  return useRef(
    flagNames.filter((flagName) => client.getBooleanValue(flagName, false)),
  ).current
}

function JesterInteractive<V, S extends ContentSpec, A extends ContentActions<S>>({
  configuration,
}: InteractiveProps<S, A, V>) {
  const { interactiveContent } = useConfiguration()
  const featureFlags = useJesterFeatureFlags()

  return (
    <RenderedInteractive
      configuration={configuration}
      renderer={renderJester}
      logger={useLogger()}
      jesterConfiguration={interactiveContent}
      featureFlags={featureFlags}
    />
  )
}

export function RenderedInteractive<V, S extends ContentSpec, A extends ContentActions<S>>({
  configuration,
  renderer,
  logger,
  jesterConfiguration,
  featureFlags = [],
}: InteractiveProps<S, A, V> & {
  renderer: typeof renderJester
  logger: Logger
  jesterConfiguration: JesterConfig
  featureFlags?: string[]
}) {
  const containerElement = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const { accent, features = [], ...rest } = configuration
    const options = {
      features: [...features, ...featureFlags, 'jesterSelfResizeFF', 'jesterFullscreenFF'],
      onError: logger.error,
      ...rest,
    } as ContentRenderOptions<S, A>

    if (!containerElement.current) throw new Error('There is no container element')
    return renderer(containerElement.current, accent, jesterConfiguration, options)
    // Depend on `configuration` as a whole rather than destructured pieces of
    // it (`accent`, `features`, `...rest`): destructuring/defaulting creates a
    // brand new object/array every render even when the contents are
    // unchanged, which previously caused this effect to tear down and
    // re-initialise the embedded game on every re-render of this component,
    // kicking students back to the game's start screen mid-session.
  }, [configuration, featureFlags, jesterConfiguration, logger, renderer])

  return (
    <div
      data-testid="jester-interactive"
      className="absolute inset-x-0 inset-y-0 flex justify-center items-center"
      ref={containerElement}
    />
  )
}

export default memo(JesterInteractive) as typeof JesterInteractive
